/**
 * Same-origin ONNX Runtime WASM URLs for browser Whisper.
 *
 * Production JS is hashed under PUBLIC_URL=storage.googleapis.com, but the
 * page origin is smartttclub.com. GCS does not send CORS, so fetching the
 * hashed .wasm from the app origin fails; transformers.js then falls back to
 * jsDelivr and Cache.put() of that 23MB body throws NetworkError.
 *
 * Serve copies from /voice-runtime/ (see scripts/copy-ort-wasm.js).
 */

export const VOICE_RUNTIME_BASE = "/voice-runtime/";

export const ORT_WASM_FILES = Object.freeze({
  mjs: "ort-wasm-simd-threaded.asyncify.mjs",
  wasm: "ort-wasm-simd-threaded.asyncify.wasm",
});

/**
 * @param {string} [href]
 * @returns {string}
 */
export function pageOrigin(href) {
  try {
    if (href) return new URL(href).origin;
    if (typeof window !== "undefined" && window.location?.origin) {
      return window.location.origin;
    }
  } catch {
    /* ignore */
  }
  return "";
}

/**
 * Path prefix for /voice-runtime on the current page origin.
 *
 * Production apex serves /voice-runtime at the site root. OVH `react-scripts
 * start` nests public/ under the CRA homepage basename
 * (/smarttt-frontend-prod) unless setupProxy remaps it — prefer that basename
 * in development so Whisper never fetches SPA HTML as WASM.
 *
 * @param {{ origin?: string, bakedPublicUrl?: string, nodeEnv?: string }} [opts]
 * @returns {string} e.g. "/voice-runtime/" or "/smarttt-frontend-prod/voice-runtime/"
 */
export function voiceRuntimeBasePath(opts = {}) {
  const nodeEnv =
    opts.nodeEnv ||
    (typeof process !== "undefined" && process.env && process.env.NODE_ENV) ||
    "";
  const baked =
    opts.bakedPublicUrl != null
      ? opts.bakedPublicUrl
      : typeof process !== "undefined" && process.env
        ? process.env.PUBLIC_URL || ""
        : "";
  const origin = opts.origin || pageOrigin();

  if (nodeEnv === "development" && baked) {
    try {
      const u = new URL(baked, origin || "http://localhost");
      const isGcsHomepage = u.hostname === "storage.googleapis.com";
      let pageHost = "";
      try {
        pageHost = origin ? new URL(origin).hostname : "";
      } catch {
        pageHost = "";
      }
      if (isGcsHomepage && pageHost && pageHost !== "storage.googleapis.com") {
        const prefix = u.pathname.replace(/\/$/, "");
        if (prefix) return `${prefix}${VOICE_RUNTIME_BASE}`;
      }
    } catch {
      /* fall through */
    }
  }
  return VOICE_RUNTIME_BASE;
}

/**
 * Point a webpack/GCS/CDN asset URL at the current page origin when it is one
 * of our hashed static/media files.
 *
 * @param {string} url
 * @param {string} [origin]
 * @returns {string}
 */
export function sameOriginAssetUrl(url, origin = pageOrigin()) {
  if (!url || typeof url !== "string") return url;
  try {
    const u = new URL(url, origin || "http://localhost");
    const mediaIdx = u.pathname.indexOf("/static/media/");
    if (
      mediaIdx >= 0 &&
      (u.pathname.endsWith(".wasm") || u.pathname.endsWith(".mjs"))
    ) {
      return `${origin}${u.pathname.slice(mediaIdx)}${u.search}`;
    }
  } catch {
    /* keep original */
  }
  return url;
}

/**
 * @param {{ origin?: string }} [opts]
 * @returns {{ mjs: string, wasm: string }}
 */
export function voiceRuntimeWasmPaths(opts = {}) {
  const origin = opts.origin || pageOrigin();
  const base = `${origin}${voiceRuntimeBasePath(opts)}`;
  return {
    mjs: `${base}${ORT_WASM_FILES.mjs}`,
    wasm: `${base}${ORT_WASM_FILES.wasm}`,
  };
}

/**
 * Configure @huggingface/transformers env for production STT.
 *
 * @param {object} env
 * @param {{ origin?: string }} [opts]
 * @returns {object}
 */
export function configureTransformersEnv(env, opts = {}) {
  if (!env) return env;
  env.allowLocalModels = false;
  env.useBrowserCache = true;
  // Cache.put of the 23MB WASM body throws NetworkError in Chrome and can
  // abort the original fetch stream so Whisper never starts.
  env.useWasmCache = false;

  const wasm = env.backends?.onnx?.wasm;
  if (wasm) {
    wasm.numThreads = 1;
    wasm.proxy = false;
    wasm.wasmPaths = voiceRuntimeWasmPaths(opts);
  }
  return env;
}
